import type { FC, ReactNode } from 'react'
import type { LegalText } from '@/types/content'

import { Container } from '@/components/generals/container'
import RichText from '@/components/generals/rich-text'
import { DraftBadge } from '@/components/sections/draft-badge'
import { cms } from '@/lib/cms'

interface LegalPageContentProps {
    legal: LegalText | null
    fallbackTitle: string
}

// Impressum and privacy policy: title from the CMS and the full text as rich text.
export const LegalPageContent: FC<LegalPageContentProps> = ({ legal, fallbackTitle }): ReactNode => {
    return (
        <section aria-labelledby='seite-titel' className='relative bg-bone pb-24 pt-14 sm:pb-32 sm:pt-20 lg:pt-24'>
            <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                <div className='flex flex-col items-start gap-6 lg:col-span-4'>
                    <p className='type-eyebrow flex items-center gap-3 text-ember'>
                        <span aria-hidden='true' className='h-px w-8 bg-current' />
                        Rechtliches
                    </p>

                    <h1
                        id='seite-titel'
                        data-mdlfy-cms={legal ? cms('rechtstexte', legal.id, 'titel') : undefined}
                        className='type-display text-display-lg'
                    >
                        {legal?.titel ?? fallbackTitle}
                    </h1>

                    {legal && !legal.bestaetigt && <DraftBadge label='Rechtstext noch nicht geprüft' />}
                </div>

                <div className='lg:col-span-7 lg:col-start-6'>
                    {legal?.inhalt
                        ? <RichText html={legal.inhalt} className='max-w-3xl text-base leading-relaxed text-ink/85' />
                        : (
                            <p className='is-placeholder self-start text-smoke'>
                                Dieser Rechtstext fehlt noch. Bitte im CMS unter Rechtstexte ergänzen.
                            </p>
                        )}
                </div>
            </Container>
        </section>
    )
}
